import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  getProfileCompleteness,
  describeIncompleteProfileForClient,
  type Profile,
  type ProfileInput,
} from '@bidmanager/shared';
import { errorMessage, request } from '../api';

/**
 * Profile editor.
 *
 * Nested entries carry their `id` through the round trip. That is what lets the
 * API update rows in place rather than deleting and recreating them — generated
 * documents reference work history and education *by id*, so churning those ids
 * while fixing a typo would orphan every past generation.
 */

type WorkEntry = ProfileInput['workHistory'][number];
type EducationEntry = ProfileInput['education'][number];

const EMPTY: ProfileInput = {
  fullName: '',
  email: '',
  phone: '',
  location: '',
  coreSkills: [],
  summary: null,
  generationProvider: null,
  workHistory: [],
  personalProjects: [],
  education: [],
};

export function ProfileEditor(): JSX.Element {
  const { profileId } = useParams<{ profileId: string }>();
  const isNew = !profileId;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<ProfileInput>(EMPTY);
  const [skillsText, setSkillsText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: existing, isLoading } = useQuery({
    queryKey: ['profile', profileId],
    queryFn: () => request<Profile>('GET', `/profiles/${profileId}`),
    enabled: !isNew,
  });

  useEffect(() => {
    if (!existing) return;
    setForm({
      fullName: existing.fullName,
      email: existing.email,
      phone: existing.phone,
      location: existing.location,
      coreSkills: existing.coreSkills,
      summary: existing.summary,
      generationProvider: existing.generationProvider,
      // ids preserved deliberately — see the note above.
      workHistory: existing.workHistory.map((entry) => ({
        id: entry.id,
        companyName: entry.companyName,
        title: entry.title,
        industry: entry.industry,
        startDate: entry.startDate,
        endDate: entry.endDate,
        projects: entry.projects.map((p) => ({
          id: p.id,
          name: p.name,
          description: p.description,
        })),
      })),
      personalProjects: existing.personalProjects.map((entry) => ({
        id: entry.id,
        name: entry.name,
        description: entry.description,
        startDate: entry.startDate,
        endDate: entry.endDate,
      })),
      education: existing.education.map((entry) => ({
        id: entry.id,
        universityName: entry.universityName,
        degree: entry.degree,
        startDate: entry.startDate,
        endDate: entry.endDate,
      })),
    });
    setSkillsText(existing.coreSkills.join(', '));
  }, [existing]);

  const save = useMutation({
    mutationFn: (input: ProfileInput) =>
      isNew
        ? request<Profile>('POST', '/profiles', input)
        : request<Profile>('PUT', `/profiles/${profileId}`, input),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['profiles'] });
      void queryClient.invalidateQueries({ queryKey: ['profile', saved.id] });
      navigate('/profiles');
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  if (!isNew && isLoading) return <p className="muted">Loading…</p>;

  const completeness = getProfileCompleteness(form);

  function update(patch: Partial<ProfileInput>): void {
    setForm((current) => ({ ...current, ...patch }));
  }

  function submit(event: React.FormEvent): void {
    event.preventDefault();
    setError(null);
    save.mutate({
      ...form,
      coreSkills: skillsText
        .split(',')
        .map((skill) => skill.trim())
        .filter(Boolean),
    });
  }

  return (
    <form onSubmit={submit}>
      <header className="page-head">
        <Link to="/profiles" className="small">
          ← Profiles
        </Link>
        <h1 style={{ marginTop: 8 }}>{isNew ? 'New profile' : form.fullName || 'Profile'}</h1>
      </header>

      {!completeness.isComplete && (
        <div className="card" style={{ borderColor: 'var(--warn)', marginBottom: 16 }}>
          <span className="warn small">
            {describeIncompleteProfileForClient(completeness.missing)}
          </span>
        </div>
      )}

      <div className="stack">
        <section className="card">
          <strong>Contact</strong>
          <div className="grid2" style={{ marginTop: 12 }}>
            <div className="field">
              <label>Full name</label>
              <input value={form.fullName} onChange={(e) => update({ fullName: e.target.value })} required />
            </div>
            <div className="field">
              <label>Email</label>
              <input type="email" value={form.email} onChange={(e) => update({ email: e.target.value })} required />
            </div>
            <div className="field">
              <label>Phone</label>
              <input value={form.phone} onChange={(e) => update({ phone: e.target.value })} required />
            </div>
            <div className="field">
              <label>Location</label>
              <input value={form.location} onChange={(e) => update({ location: e.target.value })} required />
            </div>
          </div>
          <div className="field">
            <label>Core skills (comma separated)</label>
            <input value={skillsText} onChange={(e) => setSkillsText(e.target.value)} required />
            <p className="muted small" style={{ margin: '5px 0 0' }}>
              Generated resumes may only list skills from here — nothing outside this set can appear.
            </p>
          </div>
          <div className="field">
            <label>Summary (optional)</label>
            <textarea
              value={form.summary ?? ''}
              onChange={(e) => update({ summary: e.target.value || null })}
            />
          </div>
        </section>

        <ListSection<WorkEntry>
          title="Work history"
          required={completeness.missing.includes('workHistory')}
          entries={form.workHistory}
          onChange={(workHistory) => update({ workHistory })}
          blank={{
            companyName: '',
            title: null,
            industry: null,
            startDate: '',
            endDate: null,
            projects: [],
          }}
          label={(entry) => entry.companyName || 'New role'}
          render={(entry, patch) => (
            <>
              <div className="grid2">
                <div className="field">
                  <label>Company</label>
                  <input value={entry.companyName} onChange={(e) => patch({ companyName: e.target.value })} required />
                </div>
                <div className="field">
                  <label>Title</label>
                  <input value={entry.title ?? ''} onChange={(e) => patch({ title: e.target.value || null })} />
                </div>
              </div>
              <div className="field">
                <label>Industry (optional)</label>
                <input
                  value={entry.industry ?? ''}
                  maxLength={80}
                  placeholder="What the company does, e.g. B2B payments"
                  onChange={(e) => patch({ industry: e.target.value || null })}
                />
                <p className="muted small" style={{ margin: '5px 0 0' }}>
                  Context for the writer, never printed on a resume or letter.
                </p>
              </div>
              <DateRange entry={entry} patch={patch} />

              <div className="subentry">
                <label>Projects — these become the resume bullets</label>
                {entry.projects.map((project, index) => (
                  <div key={project.id ?? index} className="field">
                    <input
                      placeholder="Project name"
                      value={project.name}
                      style={{ marginBottom: 6 }}
                      onChange={(e) =>
                        patch({
                          projects: entry.projects.map((p, i) =>
                            i === index ? { ...p, name: e.target.value } : p,
                          ),
                        })
                      }
                    />
                    <textarea
                      placeholder="What was done and what changed. Not what the company does."
                      value={project.description}
                      onChange={(e) =>
                        patch({
                          projects: entry.projects.map((p, i) =>
                            i === index ? { ...p, description: e.target.value } : p,
                          ),
                        })
                      }
                    />
                    <button
                      type="button"
                      className="ghost sm danger"
                      style={{ marginTop: 6 }}
                      onClick={() =>
                        patch({ projects: entry.projects.filter((_, i) => i !== index) })
                      }
                    >
                      Remove project
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="sm"
                  onClick={() => patch({ projects: [...entry.projects, { name: '', description: '' }] })}
                >
                  Add project
                </button>
              </div>
            </>
          )}
        />

        <ListSection<EducationEntry>
          title="Education"
          required={completeness.missing.includes('education')}
          entries={form.education}
          onChange={(education) => update({ education })}
          blank={{ universityName: '', degree: '', startDate: '', endDate: null }}
          label={(entry) => entry.universityName || 'New entry'}
          render={(entry, patch) => (
            <>
              <div className="grid2">
                <div className="field">
                  <label>Institution</label>
                  <input
                    value={entry.universityName}
                    onChange={(e) => patch({ universityName: e.target.value })}
                    required
                  />
                </div>
                <div className="field">
                  <label>Degree</label>
                  <input value={entry.degree} onChange={(e) => patch({ degree: e.target.value })} required />
                </div>
              </div>
              <DateRange entry={entry} patch={patch} />
            </>
          )}
        />
      </div>

      {error && <p className="error small">{error}</p>}

      <div style={{ display: 'flex', gap: 9, marginTop: 18 }}>
        <button className="primary" type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save profile'}
        </button>
        <Link to="/profiles">
          <button type="button" className="ghost">
            Cancel
          </button>
        </Link>
      </div>
    </form>
  );
}

/* -------------------------------------------------------------------------- */

interface DatedEntry {
  startDate: string;
  endDate: string | null;
}

function DateRange<T extends DatedEntry>({
  entry,
  patch,
}: {
  entry: T;
  patch: (value: Partial<T>) => void;
}): JSX.Element {
  return (
    <div className="grid2">
      <div className="field">
        <label>Start date</label>
        <input
          type="date"
          value={entry.startDate}
          onChange={(e) => patch({ startDate: e.target.value } as Partial<T>)}
          required
        />
      </div>
      <div className="field">
        <label>End date — leave empty for current</label>
        <input
          type="date"
          value={entry.endDate ?? ''}
          onChange={(e) => patch({ endDate: e.target.value || null } as Partial<T>)}
        />
      </div>
    </div>
  );
}

/** Generic add/remove list. Entry `id`s pass through untouched. */
function ListSection<T extends { id?: string }>({
  title,
  required,
  entries,
  blank,
  label,
  render,
  onChange,
}: {
  title: string;
  required: boolean;
  entries: T[];
  blank: T;
  label: (entry: T) => string;
  render: (entry: T, patch: (value: Partial<T>) => void) => React.ReactNode;
  onChange: (entries: T[]) => void;
}): JSX.Element {
  return (
    <section className="card">
      <div className="row" style={{ marginBottom: 12 }}>
        <strong>
          {title}
          {required && <span className="warn small"> · required</span>}
        </strong>
        <button type="button" className="sm" onClick={() => onChange([...entries, blank])}>
          Add
        </button>
      </div>

      {entries.length === 0 && <p className="muted small">Nothing added yet.</p>}

      {entries.map((entry, index) => (
        <div key={entry.id ?? `new-${index}`} className="entry">
          <div className="entry-head">
            <span className="muted small">{label(entry)}</span>
            <button
              type="button"
              className="ghost sm danger"
              onClick={() => onChange(entries.filter((_, i) => i !== index))}
            >
              Remove
            </button>
          </div>
          {render(entry, (patch) =>
            onChange(entries.map((item, i) => (i === index ? { ...item, ...patch } : item))),
          )}
        </div>
      ))}
    </section>
  );
}
